import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { HouseholdProfile } from "@/types/profile";

export type HouseholdProfilePayload = HouseholdProfile;

export const HOUSEHOLD_PROFILE_KEY = ["household-profile"] as const;

export function useHouseholdProfile() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const query = useQuery<HouseholdProfilePayload>({
    queryKey: HOUSEHOLD_PROFILE_KEY,
    queryFn: () => api<HouseholdProfilePayload>("/api/v1/households/me"),
    staleTime: 60_000,
    retry: (count, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return count < 2;
    },
  });

  const update = useMutation({
    mutationFn: (profile: HouseholdProfilePayload) =>
      api<HouseholdProfilePayload>("/api/v1/households/me", {
        method: "PATCH",
        body: profile,
      }),
    onSuccess: () => {
      toast.success(t("household.saved"));
      queryClient.invalidateQueries({ queryKey: HOUSEHOLD_PROFILE_KEY });
    },
    onError: () => {
      toast.error(t("household.error_save"));
    },
  });

  // 404 = the authenticated user has no household yet (never finished
  // onboarding, or the post-login sync failed). That is not a load error:
  // the UI must offer to create the household instead of a dead-end
  // "retry" that can only ever 404 again.
  const isMissing =
    query.error instanceof ApiError && query.error.status === 404;

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError && !isMissing,
    isMissing,
    refetch: query.refetch,
    update,
  };
}
